/**
 * "People prices": what a room type costs more per night once it holds a number of people (events.occupancy_charge,
 * event_room_prices.occupancy — a map of head count to cents per night). The highest level at or under the room's
 * head count applies alone; the database does the charging. Here: reading the map, the editor's rows and the one
 * line a guest sees on a room. No amount is worked out in the browser.
 */
import { MAX_PRICE_CENTS, parsePrice, plainAmount } from './prices';

export const MIN_PEOPLE = 2;
export const MAX_PEOPLE = 20;
/** A stored key, as the database accepts it: 2 to 20, no leading zero. */
const KEY = /^([2-9]|1[0-9]|20)$/;
export interface Level { people: number; cents: number }
export interface LevelDraft { people: string; amount: string }

export const PEOPLE_NOT_READ = `Enter a number of people from ${MIN_PEOPLE} to ${MAX_PEOPLE}.`;
export const PEOPLE_TWICE = 'This number of people is already listed.';
export const AMOUNT_NEEDED = 'Enter an amount like 25 or 25.50.';

/** The stored map as levels, lowest head count first. Anything that isn't a level is left out. */
export function levelsOf(map: unknown): Level[] {
  if (!map || typeof map !== 'object' || Array.isArray(map)) return [];
  const out: Level[] = [];
  for (const [k, v] of Object.entries(map as Record<string, unknown>)) {
    const people = Number(k);
    if (!KEY.test(k)) continue;
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 0 || v > MAX_PRICE_CENTS) continue;
    out.push({ people, cents: v });
  }
  return out.sort((a, b) => a.people - b.people);
}

/** The lowest head count that costs more, or null when no level has an amount. */
export function moreFrom(map: unknown): number | null {
  return levelsOf(map).find((l) => l.cents > 0)?.people ?? null;
}

/** What a guest reads on a room that costs more with more people. Words only, never an amount. */
export function moreLabel(people: number): string {
  return `More for ${people} or more people`;
}

export function draftsOf(map: unknown): LevelDraft[] {
  return levelsOf(map).map((l) => ({ people: String(l.people), amount: plainAmount(l.cents) }));
}

/** The next head count to offer when a level is added: one past the highest listed, from 3. */
export function nextPeople(rows: readonly LevelDraft[]): string {
  const used = rows.map((r) => Number(r.people)).filter((n) => Number.isInteger(n));
  const next = used.length ? Math.max(...used) + 1 : 3;
  return next <= MAX_PEOPLE ? String(next) : '';
}

export interface DraftErrors { people: string | null; amount: string | null }
/** The editor's rows as the map to save, or what is wrong with each row. */
export function readDrafts(rows: readonly LevelDraft[]): { ok: true; levels: Record<string, number> } | { ok: false; errors: DraftErrors[] } {
  const levels: Record<string, number> = {};
  const errors: DraftErrors[] = [];
  let bad = false;
  for (const r of rows) {
    const e: DraftErrors = { people: null, amount: null };
    const t = r.people.trim();
    const people = Number(t);
    if (!/^\d+$/.test(t) || people < MIN_PEOPLE || people > MAX_PEOPLE) e.people = PEOPLE_NOT_READ;
    else if (String(people) in levels) e.people = PEOPLE_TWICE;
    const p = parsePrice(r.amount);
    if (!p.ok || p.cents === null) e.amount = AMOUNT_NEEDED;
    if (!e.people && p.ok && p.cents !== null) levels[String(people)] = p.cents;
    if (e.people || e.amount) bad = true;
    errors.push(e);
  }
  return bad ? { ok: false, errors } : { ok: true, levels };
}

export function sameLevels(a: unknown, b: unknown): boolean {
  return JSON.stringify(levelsOf(a)) === JSON.stringify(levelsOf(b));
}
